/**
 * /settings — Profil utilisateur
 */

import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { PublicHeader, Breadcrumb } from '@/components/jurisprudence';
import { usePublicAuth } from '@/hooks/usePublicAuth';

export default function SettingsProfile() {
  const { t } = useTranslation();
  const navigate          = useNavigate();
  const { user, loading, logout } = usePublicAuth();

  useEffect(() => {
    if (!loading && !user) navigate('/login?redirect=/settings', { replace: true });
  }, [user, loading, navigate]);

  if (loading) return <Spinner />;
  if (!user)   return null;

  return (
    <>
      <Helmet><title>{t('settings.profile.page_title')}</title></Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />
        <Breadcrumb items={[{ label: t('settings.breadcrumb_home'), to: '/' }, { label: t('settings.my_account') }]} />
        <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-10">
          <SettingsNav active="profile" />

          <section className="rounded-xl border bg-card p-6 mt-6">
            <h2 className="text-base font-semibold mb-5">{t('settings.profile.title')}</h2>
            <div className="flex items-center gap-4">
              {user.photoURL ? (
                <img src={user.photoURL} alt={t('settings.profile.avatar_alt')}
                  className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center text-2xl font-bold">
                  {(user.displayName || user.email || 'U')[0].toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-semibold text-lg">{user.displayName || '—'}</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
                <div className="mt-1">
                  {user.emailVerified ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                      </svg>
                      {t('settings.profile.email_verified')}
                    </span>
                  ) : (
                    <span className="text-xs text-amber-600">{t('settings.profile.email_not_verified')}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t space-y-1">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-3">
                {t('settings.profile.account')}
              </p>
              <div className="flex items-center justify-between py-2 text-sm">
                <span className="text-muted-foreground">{t('settings.profile.name')}</span>
                <span className="font-medium">{user.displayName || '—'}</span>
              </div>
              <div className="flex items-center justify-between py-2 text-sm border-t">
                <span className="text-muted-foreground">{t('settings.profile.email')}</span>
                <span className="font-medium">{user.email}</span>
              </div>
              <div className="flex items-center justify-between py-2 text-sm border-t">
                <span className="text-muted-foreground">{t('settings.profile.login_method')}</span>
                <span className="font-medium">Google OAuth</span>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t">
              <button
                onClick={async () => { await logout(); navigate('/'); }}
                className="flex items-center gap-2 text-sm text-destructive hover:text-destructive/80 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                </svg>
                {t('common.logout')}
              </button>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

// ── Composants partagés ───────────────────────────────────────────────────────

export function SettingsNav({ active }: { active: 'profile' | 'subscription' | 'invoices' | 'api-key' }) {
  const { t } = useTranslation();
  const { subStatus } = usePublicAuth();
  const isPaid = subStatus?.plan === 'paid' && subStatus?.has_subscription;

  const links = [
    { to: '/settings',              key: 'profile',      label: t('settings.nav.profile'),      icon: UserIcon },
    { to: '/settings/subscription', key: 'subscription', label: t('settings.nav.subscription'),  icon: CreditCardIcon },
    { to: '/settings/invoices',     key: 'invoices',     label: t('settings.nav.invoices'),    icon: ReceiptIcon },
    { to: '/settings/api-key',      key: 'api-key',      label: t('settings.nav.api_key'), icon: KeyIcon },
  ].filter(({ key }) => key !== 'invoices' || !!isPaid);

  return (
    <nav className="flex gap-1 border-b pb-0">
      {links.map(({ to, key, label, icon: Icon }) => (
        <Link
          key={key}
          to={to}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            active === key
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
          }`}
        >
          <Icon className="w-4 h-4" />
          {label}
        </Link>
      ))}
    </nav>
  );
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
    </div>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
    </svg>
  );
}

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
    </svg>
  );
}

function ReceiptIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
    </svg>
  );
}

function KeyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
    </svg>
  );
}